import { francePortfolioName, lotsForPortfolio } from "./allocator";
import {
  FRANCE_MONTHS,
  GROUPS,
  MONTHLY_DUE,
  MONTHS,
  NOW_MONTH_INDEX,
  QUARTERLY_DUE,
  REPORTED_THROUGH,
} from "./complianceGroupsFixture";
import type {
  BadgeTone,
  Cadence,
  CertificateLot,
  CompanyInput,
  ConsumptionEntry,
  DeliveryState,
  DemoStateView,
  DomainInput,
  DomainKey,
  Earmarked,
  Fill,
  GroupKey,
  GroupMeta,
  KeyValue,
  MeteringPoint,
  MeteringPointInput,
  PpaDeviceInput,
  PpaInput,
  PpaKey,
  CellKind,
} from "./complianceGroupsTypes";
import { fmt, pctOf, sum } from "./format";

// ---------------------------------------------------------------------------
// View types
// ---------------------------------------------------------------------------

export interface StripCell {
  label: string;
  period: string;
  fill: Fill;
  statusLabel: string;
  tone: BadgeTone;
  detail: KeyValue[];
  note?: string;
  /** Inset marker for a delivered month well under forecast. */
  mark?: boolean;
  /** Some of the month's devices have transferred and some have not; painted as a hatch. */
  partial?: boolean;
}

export interface LegendItem {
  fill: Fill;
  label: string;
}

export interface Period {
  label: string;
  months: number[];
}

export interface DerivedMeter extends MeteringPoint {
  actual: number;
  estimated: number;
  fy: number;
  sourceLabel: string;
  sourceTone: BadgeTone;
}

export interface FranceMonthCard {
  m: number;
  label: string;
  period: string;
  consumption: number;
  locked: number;
  flexible: number;
  expected: number;
  gap: number;
  basis: "Actual" | "Estimated";
  basisTone: BadgeTone;
  statusLabel: string;
  statusWarn: boolean;
  segments: { fill: Fill; pct: string }[];
  event: string;
  hasLots: boolean;
}

export interface DerivedDomain {
  key: DomainKey;
  group: GroupKey;
  name: string;
  system: string;
  rule: string;
  structure: string;
  registry: string;
  registryCode: string;
  route: DomainInput["route"];
  supplierRequired: boolean;
  isMonthly: boolean;
  cadence: Cadence;
  note: string;
  supply: number;
  meters: DerivedMeter[];
  companies: CompanyInput[];
  consumption: { fy: number; reported: number; estimated: number };
  earmarked: Earmarked;
  earmarkedTotal: number;
  gap: number;
  lots: CertificateLot[];
  /** One cell per reporting period (12 monthly, 4 quarterly, 1 annual). */
  monthCells: StripCell[];
  months: FranceMonthCard[] | null;
}

/**
 * One row of the delivery history: a registry transfer that happened (`received`, or `late` when
 * it landed after the due date) or one that should have (`overdue`). A production period can
 * carry several rows when its devices transferred separately.
 */
export interface DeliveryEvent {
  /** Transfer date, or the due date for an outstanding transfer. */
  date: string;
  period: string;
  volume: number;
  devices: string[];
  state: "received" | "late" | "overdue";
  /** Days after the due date: how late a transfer landed, or how long an outstanding one has been due. */
  daysLate: number;
}

export interface MonthBar {
  label: string;
  pct: string;
  fill: Fill;
  title: string;
  /** Share of the bar (CSS %) painted as delivered when a month is only partly transferred. */
  deliveredPct?: string;
}

/** One production device under an agreement and where its transfers stand. */
export interface PpaDeviceView {
  id: string;
  name: string;
  tech: string;
  delivered: number;
  /** Last production period whose certificates have arrived, e.g. "Jul 2026" or "Q2 2026". */
  deliveredThrough: string;
  /** Produced volume whose transfer is past due. */
  overdue: number;
  status: string;
  tone: BadgeTone;
}

/** The earliest production period with a transfer past due. */
export interface OverdueDelivery {
  period: string;
  due: string;
  daysLate: number;
  volume: number;
  devices: string[];
  deliveredDevices: number;
  totalDevices: number;
}

export interface DerivedPpa {
  key: PpaKey;
  group: GroupKey;
  domain: DomainKey;
  name: string;
  counterparty: string;
  seller: string;
  country: string;
  cadence: string;
  system: string;
  terms: string;
  ends: string;
  registry: string;
  devices: PpaDeviceView[];
  received: number;
  stillExpected: number;
  total: number;
  pct: string;
  deliveryCells: StripCell[];
  monthBars: MonthBar[];
  monthLegend: LegendItem[];
  events: DeliveryEvent[];
  overdue: OverdueDelivery | null;
  lots: CertificateLot[];
  status: string;
  statusTone: BadgeTone;
}

export type OpenItemAction =
  | { type: "resolve" }
  | { type: "request" }
  | { type: "ppa"; key: PpaKey }
  | { type: "domain"; key: DomainKey };

/** One line of attention: what needs the reader, and where to go. Details live at the destination. */
export interface OpenItem {
  text: string;
  /** warn → something is overdue or short; pending → waiting on Soldera. */
  tone: "warn" | "pending";
  cta: string;
  action: OpenItemAction;
}

export interface PlanRow {
  when: string;
  whenWarn: boolean;
  dot: "forest" | "muted" | "warn";
  title: string;
  why: string;
  detail: string;
  chips: { name: string; value: string }[];
}

export interface BalanceSegment {
  key: string;
  label: string;
  value: number;
  fill: Fill;
}

export interface ClaimRow {
  key: DomainKey;
  name: string;
  fy: number;
  reported: number;
  estimated: number;
  monthCells: StripCell[];
  dataScope: string;
  dataVerdict: string | null;
  dataTone: BadgeTone;
  earmarkedTotal: number;
  isMonthly: boolean;
  coverScope: string;
  coverCells: StripCell[] | null;
  poolSegments: { fill: Fill; pct: string }[];
  poolDetail: KeyValue[];
  coverVerdict: string | null;
  coverDetail: string;
  attention: number;
}

/**
 * Portfolios are sized on the consumption known at allocation time and re-balanced on a weekly
 * run, not on every confirmed reading. While a domain's consumption has drifted from its sizing,
 * the claim position says when the next run is.
 */
export interface RebalanceNote {
  /** e.g. "Mon 14 Sep 2026" */
  date: string;
}

export interface GroupRollup {
  meta: GroupMeta;
  domainCount: number;
  companyCount: number;
  supplyLine: string;
  rebalance: RebalanceNote | null;
  balance: {
    supplySegs: BalanceSegment[];
    demandSegs: BalanceSegment[];
    supplyTotal: number;
    demandTotal: number;
    net: number;
    buffer: number;
  };
  plan: PlanRow[];
  incoming: OpenItem[];
  outgoing: OpenItem[];
  openActions: number;
  ppaCards: DerivedPpa[];
  domainRows: ClaimRow[];
}

export interface CompletenessRow {
  key: string;
  name: string;
  cadence: Cadence;
  cells: StripCell[];
  runs: { fill: Fill; grow: number }[];
}

export interface PeriodRow {
  monthIndex: number;
  period: string;
  total: number;
  fill: Fill;
  pointsActual: string;
  basis: string;
  basisTone: BadgeTone;
  barPct: string;
}

export interface MeterTableRow {
  id: string;
  name: string;
  entity: string;
  supplier: string;
  entry: string;
  entryTone: BadgeTone;
  actual: number;
  estimated: number;
  fy: number;
}

export interface ConsumptionView {
  totalCells: number;
  actualCells: number;
  gapCells: number;
  gapVolume: number;
  pctActual: string;
  askPrompt: string;
  rows: CompletenessRow[];
  periodLabels: string[];
  monthRows: PeriodRow[];
  meterTable: MeterTableRow[];
  baseEntries: ConsumptionEntry[];
  meterOptions: string[];
  periodOptions: string[];
  legend: LegendItem[];
  monthLegend: LegendItem[];
}

export interface MeterRowView {
  key: string;
  added: boolean;
  name: string;
  mpid: string;
  supplier: string;
  sourceLabel: string;
  sourceTone: BadgeTone;
  actual: number;
  annual: number;
  months: number[];
}

export interface CompanyView {
  key: string;
  name: string;
  registryCode: string;
  address: string;
  beneficiaryType: string;
  visibility: string;
  purpose: string;
  isReady: boolean;
  missingLabel: string;
  meterList: MeterRowView[];
  totals: { actual: number; estimated: number; fy: number };
  supplierRoute: boolean;
  supplierGroups: {
    supplier: string;
    meters: string;
    volume: number;
    /** Where Soldera stands with this supplier on the cancellation procedure. */
    status: string;
    statusTone: BadgeTone;
  }[];
}

export interface MonthDetail {
  period: string;
  scope: string;
  meters: {
    id: string;
    name: string;
    entity: string;
    basis: string;
    tone: BadgeTone;
    volume: number;
  }[];
  total: number;
  month: FranceMonthCard | null;
}

// ---------------------------------------------------------------------------
// Reporting cadence
// ---------------------------------------------------------------------------

export const STEP: Record<Cadence, number> = {
  monthly: 1,
  quarterly: 3,
  biannual: 6,
  annual: 12,
};

const PERIOD_LABEL: Record<number, (start: number) => string> = {
  1: (s) => `${MONTHS[s]} 2026`,
  3: (s) => `Q${s / 3 + 1} 2026`,
  6: (s) => `H${s / 6 + 1} 2026`,
  12: () => "2026",
};

export const periods = (step: number): Period[] =>
  Array.from({ length: 12 / step }, (_, k) => ({
    label: PERIOD_LABEL[step](k * step),
    months: Array.from({ length: step }, (_, j) => k * step + j),
  }));

export const stepOf = (mp: MeteringPoint): number => STEP[mp.cadence];

/** A set of consumption locations reads as one cadence only when they agree; mixed reverts to months. */
const stepFor = (meters: MeteringPoint[]): number => {
  const steps = [...new Set(meters.map(stepOf))];
  return steps.length === 1 ? steps[0] : 1;
};

export const periodsOf = (meters: MeteringPoint[]): Period[] =>
  periods(stepFor(meters));

/** A period is owed only once it has closed: an annual point owes nothing until the year ends. */
export const dueThrough = (mp: MeteringPoint): number =>
  Math.floor(NOW_MONTH_INDEX / stepOf(mp)) * stepOf(mp);

/**
 * How far a consumption location has reported, snapped to a period boundary. Confirmed readings
 * extend the base depth as long as they run on contiguously from it.
 */
export const throughOf = (mp: MeteringPoint): number => {
  let raw = mp.alwaysEstimated
    ? 0
    : (mp.reportedThrough ?? Math.min(REPORTED_THROUGH, dueThrough(mp)));
  while (mp.readMonths.includes(raw)) raw += 1;
  return Math.floor(raw / stepOf(mp)) * stepOf(mp);
};

/** A location-month is actual when it sits inside the reported depth or carries a confirmed reading. */
export const isActual = (mp: MeteringPoint, monthIndex: number): boolean =>
  monthIndex < throughOf(mp) || mp.readMonths.includes(monthIndex);

const entryOf = (mp: MeteringPoint): "Connected feed" | "Reported" =>
  mp.source === "Connected feed" ? "Connected feed" : "Reported";

/** How one location-month arrived. A document-confirmed month reads as reported even on a feed meter. */
const kindOf = (mp: MeteringPoint, i: number): CellKind => {
  if (mp.readMonths.includes(i)) return "reported";
  if (i < throughOf(mp))
    return entryOf(mp) === "Connected feed" ? "feed" : "reported";
  return i >= dueThrough(mp) ? "future" : "gap";
};

/** `label` is the descriptive legend text; `status` is the terse badge shown in a cell's popover. */
const CELL: Record<
  CellKind,
  { fill: Fill; label: string; status: string; tone: BadgeTone }
> = {
  feed: {
    fill: "actualFeed",
    label: "Actual · connected feed",
    status: "Actual",
    tone: "ok",
  },
  reported: {
    fill: "actualReported",
    label: "Actual · reported",
    status: "Actual",
    tone: "ok",
  },
  gap: {
    fill: "estimateOverdue",
    label: "Estimate · actual still needed",
    status: "Reading overdue",
    tone: "warn",
  },
  future: {
    fill: "estimateFuture",
    label: "Estimate · reading not yet due",
    status: "Estimated",
    tone: "pending",
  },
};

const sourceTone = (mp: MeteringPoint): BadgeTone =>
  mp.alwaysEstimated
    ? "warn"
    : mp.source === "Approved upload"
      ? "forest"
      : "ok";

/**
 * The consumption locations a domain actually has once the demo's Companies-tab edits, additions and
 * removals are applied. Every consumption figure derives from this list, so an edit moves the
 * grid, the totals and the claim cards together.
 */
const effectiveMeters = (
  input: DomainInput,
  state: DemoStateView
): MeteringPoint[] => {
  const edits = state.meterEdits[input.key] ?? {};
  const added = state.metersAdded[input.key] ?? [];
  const removed = state.metersRemoved[input.key] ?? [];
  const readings = state.readings[input.key] ?? {};
  // A confirmed reading replaces the standing estimate for that month; a location with any
  // reading is no longer "estimated from contract capacity".
  const withReadings = (mp: MeteringPointInput): MeteringPoint => {
    const read = readings[mp.id] ?? {};
    const readMonths = Object.keys(read)
      .map(Number)
      .sort((a, b) => a - b);
    // A never-reported location that now has readings keeps a zero base depth: only the months
    // actually read become actual, not the domain default.
    const firstReadings = readMonths.length > 0 && mp.alwaysEstimated;
    return {
      ...mp,
      monthly: mp.monthly.map((v, i) => read[i] ?? v),
      readMonths,
      alwaysEstimated: firstReadings ? false : mp.alwaysEstimated,
      reportedThrough: firstReadings ? 0 : mp.reportedThrough,
      source: firstReadings ? "Reported" : mp.source,
      cadence: input.cadence,
    };
  };
  const base: MeteringPointInput[] = input.meters
    .filter((mp) => !removed.includes(mp.id))
    .map((mp) => {
      const e = edits[mp.id];
      return e
        ? {
            ...mp,
            name: e.name,
            mpid: e.mpid,
            supplier: e.supplier,
            monthly: [...e.months],
          }
        : mp;
    });
  const extra: MeteringPointInput[] = added.map((a) => ({
    id: a.uid,
    name: a.name,
    mpid: a.mpid,
    entity: a.companyName,
    supplier: a.supplier,
    source: "Estimated from contract capacity",
    alwaysEstimated: true,
    added: true,
    monthly: [...a.months],
  }));
  return [...base, ...extra].map(withReadings);
};

// ---------------------------------------------------------------------------
// Domain
// ---------------------------------------------------------------------------

/**
 * One card per French consumption month. Consumption comes from the effective consumption locations
 * (so Companies-tab edits move the gap), the earmarked split from the fixture month record.
 */
const franceMonthCards = (
  lots: CertificateLot[],
  isActual: (monthIndex: number) => boolean,
  consumptionByMonth: number[]
): FranceMonthCard[] =>
  FRANCE_MONTHS.map((r) => {
    const consumption = consumptionByMonth[r.m] ?? 0;
    const earmarked = r.locked + r.flexible + r.expected;
    const gap = Math.max(0, consumption - earmarked);
    const seg = (fill: Fill, v: number) => ({
      fill,
      pct: pctOf(v, consumption),
    });
    const statusLabel =
      gap > 0
        ? `Short ${fmt(gap)}`
        : r.locked
          ? "Cancelled"
          : r.flexible
            ? "Earmarked · in account"
            : "Earmarked · expected";
    return {
      m: r.m,
      label: MONTHS[r.m],
      period: `${MONTHS[r.m]} 2026`,
      consumption,
      locked: r.locked,
      flexible: r.flexible,
      expected: r.expected,
      gap,
      basis: isActual(r.m) ? "Actual" : "Estimated",
      basisTone: isActual(r.m) ? "forest" : "pending",
      statusLabel,
      statusWarn: gap > 0,
      segments: [
        seg("cancelled", r.locked),
        seg("inAccount", r.flexible),
        seg("expected", r.expected),
        seg("shortfall", gap),
      ],
      event: r.event,
      hasLots: lotsForPortfolio(lots, francePortfolioName(r.m)).length > 0,
    };
  });

const domainMonthCells = (meters: MeteringPoint[]): StripCell[] =>
  periodsOf(meters).map(({ label, months }) => {
    let est = 0;
    let met = 0;
    for (const mp of meters) {
      for (const i of months) {
        if (isActual(mp, i)) met += mp.monthly[i];
        else est += mp.monthly[i];
      }
    }
    const nActual = meters.filter((mp) =>
      months.every((i) => isActual(mp, i))
    ).length;
    const overdue = meters.some((mp) =>
      months.some((i) => !isActual(mp, i) && i < dueThrough(mp))
    );
    const actual = meters.length > 0 && nActual === meters.length;
    return {
      label: label.replace(" 2026", ""),
      period: label,
      fill: actual
        ? "actualFeed"
        : overdue
          ? "estimateOverdue"
          : "estimateFuture",
      statusLabel: actual
        ? "Actual"
        : overdue
          ? "Reading overdue"
          : "Estimated",
      tone: actual ? "ok" : overdue ? "warn" : "pending",
      detail: [
        { k: "Consumption", v: `${fmt(met + est)} MWh` },
        { k: "Points actual", v: `${nActual} of ${meters.length}` },
        { k: "Actual", v: met ? `${fmt(met)} MWh` : "—" },
        { k: "Estimate", v: est ? `${fmt(est)} MWh` : "—" },
      ],
    };
  });

/** MWh of a location's year that rests on actual data. */
const actualVolume = (mp: MeteringPoint): number =>
  sum(mp.monthly.filter((_, i) => isActual(mp, i)));

export const deriveDomain = (
  input: DomainInput,
  lots: CertificateLot[],
  state: DemoStateView
): DerivedDomain => {
  const meters = effectiveMeters(input, state);
  const fy = sum(meters.map((mp) => sum(mp.monthly)));
  const reported = sum(meters.map(actualVolume));
  const volume = (status: CertificateLot["status"]) =>
    sum(lots.filter((l) => l.status === status).map((l) => l.n));
  // France carries a fixed split; an annual domain's split is whatever the allocator put in its
  // portfolio, so it can never drift from the certificates table.
  const earmarked: Earmarked = input.earmarked ?? {
    locked: volume("Cancelled"),
    flexible: volume("Available"),
    expected: Math.max(
      0,
      input.supply - volume("Cancelled") - volume("Available")
    ),
  };
  const earmarkedTotal =
    earmarked.locked + earmarked.flexible + earmarked.expected;
  return {
    key: input.key,
    group: input.group,
    name: input.name,
    system: input.system,
    rule: input.rule,
    structure: input.structure,
    registry: input.registry,
    registryCode: input.registryCode,
    route: input.route,
    supplierRequired: input.route === "Supplier-coordinated",
    isMonthly: input.isMonthly,
    cadence: input.cadence,
    note: input.note,
    supply: input.supply,
    meters: meters.map((mp) => {
      const actual = actualVolume(mp);
      return {
        ...mp,
        actual,
        estimated: sum(mp.monthly) - actual,
        fy: sum(mp.monthly),
        sourceLabel: mp.alwaysEstimated ? "Estimated" : mp.source,
        sourceTone: sourceTone(mp),
      };
    }),
    companies: input.companies,
    consumption: { fy, reported, estimated: fy - reported },
    earmarked,
    earmarkedTotal,
    gap: fy - earmarkedTotal,
    lots,
    monthCells: domainMonthCells(meters),
    months: input.isMonthly
      ? franceMonthCards(
          lots,
          (m) => meters.length > 0 && meters.every((mp) => isActual(mp, m)),
          MONTHS.map((_, m) => sum(meters.map((mp) => mp.monthly[m])))
        )
      : null,
  };
};

/** The first French month with a positive gap, or null when nothing is short. */
export const franceShortMonth = (
  domain: DerivedDomain
): FranceMonthCard | null => domain.months?.find((m) => m.gap > 0) ?? null;

// ---------------------------------------------------------------------------
// PPA
// ---------------------------------------------------------------------------

const DELIVERY: Record<DeliveryState, { fill: Fill; tone: BadgeTone }> = {
  received: { fill: "inAccount", tone: "ok" },
  partial: { fill: "shortfall", tone: "warn" },
  behind: { fill: "shortfall", tone: "warn" },
  waiting: { fill: "expected", tone: "pending" },
  future: { fill: "estimateFuture", tone: "pending" },
};
const BAR_FILL: Record<DeliveryState, Fill> = {
  received: "actualFeed",
  partial: "estimateOverdue",
  behind: "estimateOverdue",
  waiting: "expected",
  future: "estimateFuture",
};

/** The demo's "today": 10 September 2026. */
const TODAY = Date.UTC(2026, 8, 10);
const MONTH_INDEX: Record<string, number> = Object.fromEntries(
  MONTHS.map((m, i) => [m, i])
);
/** "28 Aug 2026" → epoch ms (UTC midnight). */
const parseDay = (human: string): number => {
  const [day, mon, year] = human.split(" ");
  return Date.UTC(Number(year), MONTH_INDEX[mon], Number(day));
};
const daysBetween = (from: number, to: number): number =>
  Math.round((to - from) / 86_400_000);
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** The Monday after `epochMs` (a Monday itself rolls to the next one), as "Mon 14 Sep 2026". */
const nextMonday = (epochMs: number): string => {
  const date = new Date(epochMs);
  const ahead = (8 - date.getUTCDay()) % 7 || 7;
  date.setUTCDate(date.getUTCDate() + ahead);
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

const dueDate = (p: PpaInput, i: number): string =>
  p.quarterly ? QUARTERLY_DUE[Math.floor(i / 3)] : MONTHLY_DUE[i];
/** A transfer is overdue once its due date lies behind the demo's today. */
const isPastDue = (p: PpaInput, i: number): boolean =>
  parseDay(dueDate(p, i)) < TODAY;

const deviceNames = (devices: PpaDeviceInput[]): string[] =>
  devices.map((d) => d.name);
const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

export const derivePpa = (p: PpaInput, lots: CertificateLot[]): DerivedPpa => {
  const n = p.devices.length;
  const deliveredDevices = (i: number) =>
    p.devices.filter((d) => i < d.deliveredThrough);
  const outstandingDevices = (i: number) =>
    p.devices.filter((d) => i >= d.deliveredThrough);
  const produced = (i: number) =>
    p.devices.some((d) => d.actual[i] !== undefined);
  const delivered = MONTHS.map((_, i) =>
    sum(deliveredDevices(i).map((d) => d.actual[i] ?? 0))
  );
  // Still to come for a month: the seller's reported production of the devices that have not
  // transferred yet, or the forecast while nothing has been produced at all.
  const outstanding = MONTHS.map((_, i) =>
    produced(i)
      ? sum(outstandingDevices(i).map((d) => d.actual[i] ?? 0))
      : p.forecast[i]
  );
  const monthVolume = (i: number) => delivered[i] + outstanding[i];
  const received = sum(delivered);
  const stillExpected = sum(outstanding);
  const total = received + stillExpected;

  const stateOf = (i: number): DeliveryState => {
    const k = deliveredDevices(i).length;
    if (k === n) return "received";
    if (k > 0) return "partial";
    if (isPastDue(p, i)) return "behind";
    return i < NOW_MONTH_INDEX ? "waiting" : "future";
  };
  const overdueAt = (i: number) => stateOf(i) !== "received" && isPastDue(p, i);
  // A partial month whose rest is not yet due reads as "in progress", not as a problem.
  const cellFill = (i: number): Fill =>
    stateOf(i) === "partial" && !overdueAt(i)
      ? "expected"
      : DELIVERY[stateOf(i)].fill;
  const cellTone = (i: number): BadgeTone =>
    stateOf(i) === "partial" && !overdueAt(i)
      ? "pending"
      : DELIVERY[stateOf(i)].tone;
  const barFill = (i: number): Fill =>
    stateOf(i) === "partial" && !overdueAt(i)
      ? "expected"
      : BAR_FILL[stateOf(i)];
  const deliveredShare = (i: number) =>
    Math.round((delivered[i] / (monthVolume(i) || 1)) * 100);

  const devPct = (i: number) =>
    ((delivered[i] - p.forecast[i]) / p.forecast[i]) * 100;
  const devLabel = (i: number) =>
    `${devPct(i) >= 0 ? "+" : "−"}${Math.abs(devPct(i)).toFixed(1)}%`;

  // The badge names the state only; every specific goes into its own single-value row so the
  // popover reads as an aligned key/value list.
  const STATUS_LABEL: Record<DeliveryState, string> = {
    received: "Delivered",
    partial: "Partly delivered",
    behind: "Overdue",
    waiting: "Produced · not yet due",
    future: "Not produced yet",
  };
  const deliveryCells: StripCell[] = MONTHS.map((m, i) => {
    const st = stateOf(i);
    const detail: KeyValue[] = [
      { k: "Forecast", v: `${fmt(p.forecast[i])} MWh` },
    ];
    if (st === "received") {
      detail.push(
        { k: "Delivered", v: `${fmt(delivered[i])} MWh` },
        { k: "Against forecast", v: devLabel(i) }
      );
    } else if (st === "partial") {
      detail.push(
        { k: "Delivered", v: `${fmt(delivered[i])} MWh` },
        { k: "Devices delivered", v: `${deliveredDevices(i).length} of ${n}` },
        { k: "Outstanding", v: `${fmt(outstanding[i])} MWh` },
        { k: "Awaiting", v: deviceNames(outstandingDevices(i)).join(", ") },
        { k: overdueAt(i) ? "Was due" : "Due", v: dueDate(p, i) }
      );
    } else if (st === "behind") {
      detail.push(
        { k: "Produced", v: `${fmt(outstanding[i])} MWh` },
        { k: "Was due", v: dueDate(p, i) }
      );
    } else if (st === "waiting") {
      detail.push({ k: "Transfer due", v: dueDate(p, i) });
    }
    return {
      label: m[0],
      period: `${m} 2026`,
      fill: cellFill(i),
      tone: cellTone(i),
      statusLabel: STATUS_LABEL[st],
      detail,
      partial: st === "partial" || undefined,
      // A month more than 15% under forecast gets an inset rule, not a colour change.
      mark: st === "received" && devPct(i) <= -15,
    };
  });

  const max =
    Math.max(
      ...MONTHS.map((_, i) => Math.max(p.forecast[i], monthVolume(i)))
    ) || 1;
  const monthBars: MonthBar[] = MONTHS.map((m, i) => {
    const st = stateOf(i);
    const v = monthVolume(i);
    const title =
      st === "received"
        ? `${m} 2026 · ${fmt(delivered[i])} MWh delivered · forecast ${fmt(p.forecast[i])} MWh · ${devLabel(i)}`
        : st === "partial"
          ? `${m} 2026 · ${fmt(delivered[i])} MWh delivered, ${fmt(outstanding[i])} MWh outstanding from ${deviceNames(outstandingDevices(i)).join(", ")} · transfer due ${dueDate(p, i)}${overdueAt(i) ? ", overdue" : ""}`
          : st === "behind"
            ? `${m} 2026 · ${fmt(v)} MWh produced · transfer due ${dueDate(p, i)}, overdue`
            : st === "waiting"
              ? `${m} 2026 · ${fmt(v)} MWh forecast · transfer due ${dueDate(p, i)}`
              : `${m} 2026 · ${fmt(v)} MWh forecast · not produced yet`;
    return {
      label: m,
      pct: `${Math.max(4, Math.round((v / max) * 100))}%`,
      fill: barFill(i),
      title,
      deliveredPct: st === "partial" ? `${deliveredShare(i)}%` : undefined,
    };
  });
  // A partly delivered month shows both its delivered and its outstanding colour, so the legend
  // lists those two states rather than a third one.
  const states = new Set<DeliveryState>();
  MONTHS.forEach((_, i) => {
    const st = stateOf(i);
    if (st === "partial") {
      states.add("received");
      states.add(overdueAt(i) ? "behind" : "waiting");
    } else {
      states.add(st);
    }
  });
  const monthLegend: LegendItem[] = (
    [
      ["received", "Delivered"],
      ["behind", "Overdue"],
      ["waiting", "Produced · transfer not yet due"],
      ["future", "Forecast · not produced yet"],
    ] as [DeliveryState, string][]
  )
    .filter(([st]) => states.has(st))
    .map(([st, label]) => ({ fill: BAR_FILL[st], label }));

  // Delivery history: one row per registry transfer (a period splits into several rows when its
  // devices transferred on different dates) plus one row per transfer that is past due.
  const periods = p.quarterly
    ? [0, 1, 2, 3].map((q) => ({
        label: `Q${q + 1} 2026 · ${MONTHS[q * 3]}–${MONTHS[q * 3 + 2]}`,
        months: [q * 3, q * 3 + 1, q * 3 + 2],
        due: QUARTERLY_DUE[q],
      }))
    : MONTHS.map((m, i) => ({
        label: `${m} 2026`,
        months: [i],
        due: MONTHLY_DUE[i],
      }));
  const events: DeliveryEvent[] = [];
  for (const period of periods) {
    const lastMonth = period.months[period.months.length - 1];
    const periodVolume = (d: PpaDeviceInput) =>
      sum(period.months.map((i) => d.actual[i] ?? 0));
    const transfers = new Map<string, PpaDeviceInput[]>();
    for (const d of p.devices.filter((d) => lastMonth < d.deliveredThrough)) {
      const date = d.deliveredOn?.[period.months[0]] ?? period.due;
      transfers.set(date, [...(transfers.get(date) ?? []), d]);
    }
    for (const [date, devices] of transfers) {
      const daysLate = daysBetween(parseDay(period.due), parseDay(date));
      events.push({
        date,
        period: period.label,
        volume: sum(devices.map(periodVolume)),
        devices: deviceNames(devices),
        state: daysLate > 0 ? "late" : "received",
        daysLate: Math.max(0, daysLate),
      });
    }
    const pending = p.devices.filter((d) => lastMonth >= d.deliveredThrough);
    if (pending.length && isPastDue(p, period.months[0])) {
      events.push({
        date: period.due,
        period: period.label,
        volume: sum(pending.map(periodVolume)),
        devices: deviceNames(pending),
        state: "overdue",
        daysLate: daysBetween(parseDay(period.due), TODAY),
      });
    }
  }
  events.sort(
    (a, b) =>
      Number(b.state === "overdue") - Number(a.state === "overdue") ||
      parseDay(b.date) - parseDay(a.date)
  );

  const firstOverdue = MONTHS.findIndex((_, i) => overdueAt(i));
  const overdue: OverdueDelivery | null =
    firstOverdue === -1
      ? null
      : {
          period: `${MONTHS[firstOverdue]} 2026`,
          due: dueDate(p, firstOverdue),
          daysLate: daysBetween(parseDay(dueDate(p, firstOverdue)), TODAY),
          volume: outstanding[firstOverdue],
          devices: deviceNames(outstandingDevices(firstOverdue)),
          deliveredDevices: deliveredDevices(firstOverdue).length,
          totalDevices: n,
        };

  const periodLabel = (through: number) =>
    through === 0
      ? "—"
      : p.quarterly
        ? `Q${Math.ceil(through / 3)} 2026`
        : `${MONTHS[through - 1]} 2026`;
  const devices: PpaDeviceView[] = p.devices.map((d) => {
    const lateMonths = MONTHS.map((_, i) => i).filter(
      (i) =>
        i >= d.deliveredThrough && isPastDue(p, i) && d.actual[i] !== undefined
    );
    const overdueVolume = sum(lateMonths.map((i) => d.actual[i]));
    return {
      id: d.id,
      name: d.name,
      tech: p.tech,
      delivered: sum(d.actual.slice(0, d.deliveredThrough)),
      deliveredThrough: periodLabel(d.deliveredThrough),
      overdue: overdueVolume,
      status: lateMonths.length
        ? `${MONTHS[lateMonths[0]]} 2026 overdue · ${fmt(overdueVolume)} MWh`
        : "Up to date",
      tone: lateMonths.length ? "warn" : "ok",
    };
  });

  const [seller, country] = p.counterparty.split(" · ");
  return {
    key: p.key,
    group: p.group,
    domain: p.domain,
    name: p.name,
    counterparty: p.counterparty,
    seller,
    country,
    cadence: p.cadence,
    system: p.system,
    terms: p.terms,
    ends: p.ends,
    registry: p.registry,
    devices,
    received,
    stillExpected,
    total,
    pct: `${Math.round((received / total) * 100)}%`,
    deliveryCells,
    monthBars,
    monthLegend,
    events,
    overdue,
    lots,
    status:
      overdue === null
        ? "On schedule"
        : overdue.deliveredDevices > 0
          ? `Partly delivered · ${plural(overdue.devices.length, "device")} overdue`
          : "Transfer overdue",
    statusTone: overdue === null ? "ok" : "warn",
  };
};

// ---------------------------------------------------------------------------
// Group roll-up
// ---------------------------------------------------------------------------

const ORDER: DomainKey[] = ["fr", "de", "es", "se", "uk"];
const byOrder = (a: { key: DomainKey }, b: { key: DomainKey }) =>
  ORDER.indexOf(a.key) - ORDER.indexOf(b.key);
const Q1_MONTHS = ["2026-01", "2026-02", "2026-03", "2026-04"];

const openItems = (
  domains: DerivedDomain[],
  ppas: DerivedPpa[],
  state: DemoStateView
): { incoming: OpenItem[]; outgoing: OpenItem[] } => {
  const fr = domains.find((d) => d.key === "fr");
  if (!fr) return { incoming: [], outgoing: [] };
  const short = franceShortMonth(fr);
  const incoming: OpenItem[] = ppas.flatMap((p) =>
    p.overdue
      ? [
          {
            text: `${p.name} · ${p.overdue.period} transfer ${plural(p.overdue.daysLate, "day")} overdue`,
            tone: "warn" as const,
            cta: "Open agreement",
            action: { type: "ppa" as const, key: p.key },
          },
        ]
      : []
  );
  const shortfall: OpenItem[] = short
    ? [
        state.requestSubmitted
          ? {
              text: `France · ${short.label} 2026 sourcing request pending with Soldera`,
              tone: "pending",
              cta: "View request",
              action: { type: "request" },
            }
          : {
              text: `France · ${short.label} 2026 short by ${fmt(short.gap)} MWh`,
              tone: "warn",
              cta: "Source certificates",
              action: { type: "resolve" },
            },
      ]
    : [];
  // A location still running on its onboarding estimate has never reported: name it until a
  // confirmed reading arrives.
  const unreported: OpenItem[] = domains.flatMap((d) =>
    d.meters
      .filter((mp) => mp.alwaysEstimated)
      .map((mp) => ({
        text: `${d.name} · ${mp.name} has not reported consumption since ${MONTHS[0]} 2026`,
        tone: "warn" as const,
        cta: `Open ${d.name}`,
        action: { type: "domain" as const, key: d.key },
      }))
  );
  const outgoing: OpenItem[] = [...shortfall, ...unreported];
  return { incoming, outgoing };
};

const planRows = (domains: DerivedDomain[]): PlanRow[] => {
  const openIn = (d: DerivedDomain, months?: string[]) =>
    sum(
      d.lots
        .filter(
          (c) =>
            c.status === "Available" && (!months || months.includes(c.month))
        )
        .map((c) => c.n)
    );
  const chips = (rows: { name: string; v: number }[]) =>
    rows
      .filter((r) => r.v > 0)
      .map((r) => ({ name: r.name, value: `${fmt(r.v)} MWh` }));
  const rows: PlanRow[] = [];
  const sorted = [...domains].sort(byOrder);

  if (domains.length > 1) {
    rows.push({
      when: "Recurring",
      whenWarn: false,
      dot: "forest",
      title: "Automatic portfolio optimization",
      why: "Ensures compliance and reduces the cost of any potential shortfall.",
      detail:
        "Soldera takes market specifics into account and allocates your incoming GOs to countries on a priority order.",
      chips: [
        {
          name: "Priority order",
          value: sorted.map((d) => d.name).join(" → "),
        },
      ],
    });
  }
  const monthly = domains.filter((d) => d.isMonthly);
  if (monthly.length) {
    rows.push({
      when: "Recurring",
      whenWarn: false,
      dot: "forest",
      title: `Monthly cancellation · ${monthly.map((d) => d.name).join(", ")}`,
      why: "Ensures early and timely compliance.",
      detail:
        "Executed one to two months after each month's consumption data is reported, once the reading is final.",
      chips: chips(
        monthly.map((d) => ({
          name: `${d.name} · cancelled to date`,
          v: d.earmarked.locked,
        }))
      ),
    });
  }
  const coord = domains.filter((d) => d.route === "Supplier-coordinated");
  if (coord.length) {
    rows.push({
      when: "In progress",
      whenWarn: false,
      dot: "muted",
      title: `Establishing cancellation procedures · ${coord.map((d) => d.name).join(", ")}`,
      why: "These countries require cooperation with their electricity suppliers to cancel.",
      detail:
        "Soldera is agreeing the procedure with each supplier and will copy you on the correspondence.",
      chips: coord.flatMap((d) =>
        [...new Set(d.meters.map((mp) => mp.supplier))].map((sup) => ({
          name: sup,
          value: d.name,
        }))
      ),
    });
  }
  const q1 = chips(
    sorted.map((d) => ({ name: d.name, v: openIn(d, Q1_MONTHS) }))
  );
  if (q1.length) {
    rows.push({
      when: "Nov 2026",
      whenWarn: true,
      dot: "warn",
      title: "Cancel January to April certificates",
      why: "Avoids certificate expiration and logistical risk on the year's oldest certificates.",
      detail:
        "Brought forward rather than held to the annual run. A month that is still short cannot be cancelled until it is covered.",
      chips: q1,
    });
  }
  const rest = chips(
    sorted
      .filter((d) => !d.isMonthly)
      .map((d) => ({
        name: d.name,
        v: d.supply - d.earmarked.locked - openIn(d, Q1_MONTHS),
      }))
  );
  if (rest.length) {
    rows.push({
      when: "Feb 2027",
      whenWarn: false,
      dot: "forest",
      title: "Cancel the remaining annual portfolios",
      why: "Cancelling as early as possible reduces risk.",
      detail:
        "Executed per country as soon as that country's full-year consumption data is in.",
      chips: rest,
    });
  }
  return rows;
};

const claimRow = (d: DerivedDomain): ClaimRow => {
  const cover = d.earmarkedTotal;
  const pct = (v: number) =>
    cover ? `${((v / cover) * 100).toFixed(0)}%` : "0%";
  // Only periods that should already have been reported count as due.
  const dueCells = sum(
    d.meters.map(
      (mp) =>
        periods(stepOf(mp)).filter(
          (p) =>
            !p.months.every((m) => isActual(mp, m)) &&
            p.months[p.months.length - 1] < dueThrough(mp)
        ).length
    )
  );
  const coverCells: StripCell[] | null = d.months
    ? d.months.map((m) => {
        const state =
          m.gap > 0
            ? "short"
            : m.locked
              ? "settled"
              : m.flexible
                ? "held"
                : "expected";
        const fill: Fill =
          state === "short"
            ? "shortfall"
            : state === "settled"
              ? "cancelled"
              : state === "held"
                ? "inAccount"
                : "expected";
        return {
          label: m.label[0],
          period: m.period,
          fill,
          statusLabel:
            state === "short"
              ? "Short"
              : state === "settled"
                ? "Cancelled"
                : state === "held"
                  ? "In account"
                  : "Awaiting delivery",
          tone:
            state === "short"
              ? "warn"
              : state === "settled"
                ? "forest"
                : state === "held"
                  ? "ok"
                  : "pending",
          detail: [
            { k: "Consumption", v: `${fmt(m.consumption)} MWh` },
            { k: "Cancelled", v: m.locked ? `${fmt(m.locked)} MWh` : "—" },
            { k: "In account", v: m.flexible ? `${fmt(m.flexible)} MWh` : "—" },
            {
              k: "Awaiting delivery",
              v: m.expected ? `${fmt(m.expected)} MWh` : "—",
            },
            { k: "Gap", v: m.gap ? `${fmt(m.gap)} MWh` : "0" },
          ],
          note: m.event,
        };
      })
    : null;
  const shortMonths = d.months?.filter((m) => m.gap > 0).length ?? 0;
  return {
    key: d.key,
    name: d.name,
    fy: d.consumption.fy,
    reported: d.consumption.reported,
    estimated: d.consumption.estimated,
    monthCells: d.monthCells,
    dataScope: d.cadence,
    dataVerdict: dueCells
      ? `${dueCells} reading${dueCells === 1 ? "" : "s"} due`
      : null,
    dataTone: d.meters.some((mp) => mp.alwaysEstimated) ? "warn" : "pending",
    earmarkedTotal: d.earmarkedTotal,
    isMonthly: d.isMonthly,
    coverScope: d.isMonthly ? "monthly portfolios" : "annual portfolio",
    coverCells,
    poolSegments: [
      { fill: "cancelled", pct: pct(d.earmarked.locked) },
      { fill: "inAccount", pct: pct(d.earmarked.flexible) },
      { fill: "expected", pct: pct(d.earmarked.expected) },
    ],
    poolDetail: [
      { k: "Consumption", v: `${fmt(d.consumption.fy)} MWh` },
      {
        k: "Cancelled",
        v: d.earmarked.locked ? `${fmt(d.earmarked.locked)} MWh` : "—",
      },
      {
        k: "In account",
        v: d.earmarked.flexible ? `${fmt(d.earmarked.flexible)} MWh` : "—",
      },
      {
        k: "Awaiting delivery",
        v: d.earmarked.expected ? `${fmt(d.earmarked.expected)} MWh` : "—",
      },
      { k: "Gap", v: d.gap > 0 ? `${fmt(d.gap)} MWh` : "0" },
    ],
    coverVerdict:
      d.gap > 0
        ? d.isMonthly
          ? `${shortMonths} of 12 months short`
          : `Short ${fmt(d.gap)} MWh`
        : null,
    coverDetail: `${fmt(d.earmarked.locked)} cancelled · ${fmt(d.earmarked.flexible)} in account · ${fmt(d.earmarked.expected)} undelivered`,
    attention:
      (d.gap > 0 ? 100 : 0) +
      (d.meters.some((mp) => mp.alwaysEstimated) ? 10 : 0),
  };
};

export const deriveRollup = (
  group: GroupKey,
  domains: DerivedDomain[],
  ppas: DerivedPpa[],
  state: DemoStateView
): GroupRollup => {
  const actualDemand = sum(domains.map((d) => d.consumption.reported));
  const fyDemand = sum(domains.map((d) => d.consumption.fy));
  const settled = sum(domains.map((d) => d.earmarked.locked));
  const poolForecast = sum(ppas.map((p) => p.total));
  const poolDelivered = sum(ppas.map((p) => p.received));
  const account = poolDelivered - settled;
  const expected = poolForecast - poolDelivered;
  const supplyTotal = settled + account + expected;
  const net = supplyTotal - fyDemand;
  const buffer = Math.max(0, poolForecast - sum(domains.map((d) => d.supply)));
  const seg = (
    key: string,
    label: string,
    value: number,
    fill: Fill
  ): BalanceSegment => ({ key, label, value, fill });
  const items =
    group === "eecs"
      ? openItems(domains, ppas, state)
      : { incoming: [], outgoing: [] };
  const companyCount = sum(domains.map((d) => d.companies.length));
  // Portfolios were sized before any reading was confirmed; a confirmed reading anywhere in the
  // group means the sizing is stale until the weekly run.
  const readingsConfirmed = domains.some((d) =>
    d.meters.some((mp) => mp.readMonths.length > 0)
  );
  const rebalance: RebalanceNote | null = readingsConfirmed
    ? { date: nextMonday(TODAY) }
    : null;
  return {
    meta: GROUPS[group],
    domainCount: domains.length,
    companyCount,
    supplyLine: `${ppas.length} ${ppas.length === 1 ? "agreement" : "agreements"} · ${fmt(poolForecast)} MWh forecast · ${fmt(poolDelivered)} MWh delivered`,
    rebalance,
    balance: {
      supplySegs: [
        seg("settled", "Cancelled", settled, "cancelled"),
        seg("account", "In account", account, "inAccount"),
        seg("expected", "Contracted, not delivered", expected, "expected"),
        seg(
          "short",
          "Shortfall",
          Math.max(0, fyDemand - supplyTotal),
          "shortfall"
        ),
      ].filter((s) => s.value > 0),
      demandSegs: [
        seg("reported", "Metered consumption", actualDemand, "metered"),
        seg(
          "estimated",
          "Estimated remainder",
          fyDemand - actualDemand,
          "estimated"
        ),
        seg(
          "surplus",
          "Surplus",
          Math.max(0, supplyTotal - fyDemand),
          "surplus"
        ),
      ].filter((s) => s.value > 0),
      supplyTotal,
      demandTotal: fyDemand,
      net,
      buffer,
    },
    plan: planRows(domains),
    incoming: items.incoming,
    outgoing: items.outgoing,
    openActions: items.incoming.length + items.outgoing.length,
    ppaCards: ppas,
    domainRows: [...domains]
      .sort(byOrder)
      .map(claimRow)
      .sort((a, b) => b.attention - a.attention),
  };
};

// ---------------------------------------------------------------------------
// Consumption tab
// ---------------------------------------------------------------------------

export const deriveConsumption = (domain: DerivedDomain): ConsumptionView => {
  const meters = domain.meters;
  let actualCells = 0;
  let gapCells = 0;
  let gapVolume = 0;
  let totalCells = 0;
  const rows: CompletenessRow[] = meters.map((mp) => {
    const cells: StripCell[] = MONTHS.map((label, i) => {
      const kind = kindOf(mp, i);
      return {
        label,
        period: `${label} 2026 · ${mp.name}`,
        fill: CELL[kind].fill,
        statusLabel: CELL[kind].status,
        tone: CELL[kind].tone,
        detail: [
          { k: "Volume", v: `${fmt(mp.monthly[i])} MWh` },
          {
            k: "Basis",
            v: kind === "gap" || kind === "future" ? "Estimate" : "Actual",
          },
          ...(kind === "feed" || kind === "reported"
            ? [{ k: "Source", v: mp.source }]
            : []),
          { k: "Supplier", v: mp.supplier },
        ],
      };
    });
    for (const p of periods(stepOf(mp))) {
      totalCells++;
      const kinds = p.months.map((i) => kindOf(mp, i));
      if (kinds.every((k) => k === "feed" || k === "reported")) actualCells++;
      else if (kinds.some((k) => k === "gap")) {
        gapCells++;
        gapVolume += sum(p.months.map((i) => mp.monthly[i]));
      }
    }
    // Collapse consecutive months of the same basis into one segment.
    const runs: { fill: Fill; grow: number }[] = [];
    cells.forEach((c) => {
      const last = runs[runs.length - 1];
      if (last && last.fill === c.fill) last.grow += 1;
      else runs.push({ fill: c.fill, grow: 1 });
    });
    return { key: mp.id, name: mp.name, cadence: mp.cadence, cells, runs };
  });

  const domPeriods = periodsOf(meters);
  const monthRows: PeriodRow[] = domPeriods.map(({ label, months }) => {
    let total = 0;
    let nActual = 0;
    let overdue = false;
    for (const mp of meters) {
      let allActual = true;
      for (const j of months) {
        const k = kindOf(mp, j);
        total += mp.monthly[j];
        if (k !== "feed" && k !== "reported") allActual = false;
        if (k === "gap") overdue = true;
      }
      if (allActual) nActual++;
    }
    const actual = meters.length > 0 && nActual === meters.length;
    return {
      monthIndex: months[0],
      period: label,
      total,
      fill: actual
        ? "actualFeed"
        : overdue
          ? "estimateOverdue"
          : "estimateFuture",
      pointsActual: `${nActual} of ${meters.length}`,
      basis: actual ? "Actual" : overdue ? "Estimated · overdue" : "Estimated",
      basisTone: actual ? "ok" : overdue ? "warn" : "pending",
      barPct: "0%",
    };
  });
  const max = Math.max(...monthRows.map((r) => r.total)) || 1;
  monthRows.forEach((r) => {
    r.barPct = `${Math.max(12, Math.round((r.total / max) * 100))}%`;
  });

  // Hand-entered rows only, one per reporting period: feed months are never editable by hand.
  const baseEntries: ConsumptionEntry[] = meters
    .filter((mp) => entryOf(mp) !== "Connected feed")
    .flatMap((mp) =>
      periods(stepOf(mp))
        .filter((p) => p.months.every((i) => isActual(mp, i)))
        .map((p) => ({
          name: mp.name,
          period: p.label,
          value: fmt(sum(p.months.map((i) => mp.monthly[i]))),
        }))
    );

  return {
    totalCells,
    actualCells,
    gapCells,
    gapVolume,
    pctActual: totalCells
      ? `${Math.round((actualCells / totalCells) * 100)}%`
      : "0%",
    askPrompt: gapCells
      ? `${domain.name} has ${gapCells} reporting period${gapCells === 1 ? "" : "s"} still standing on an onboarding estimate. Attach a document or tell me the readings and I will place them.`
      : `${domain.name} has no readings outstanding. Attach a document or tell me a reading and I will place it.`,
    rows,
    periodLabels: domPeriods.map((p) => p.label.replace(" 2026", "")),
    monthRows,
    meterTable: meters.map((mp) => ({
      id: mp.id,
      name: mp.name,
      entity: mp.entity,
      supplier: mp.supplier,
      entry: mp.alwaysEstimated ? "Not yet reported" : entryOf(mp),
      entryTone: mp.alwaysEstimated
        ? "warn"
        : entryOf(mp) === "Connected feed"
          ? "ok"
          : "pending",
      actual: mp.alwaysEstimated ? 0 : mp.actual,
      estimated: mp.alwaysEstimated ? mp.fy : mp.estimated,
      fy: mp.fy,
    })),
    baseEntries,
    meterOptions: meters.map((mp) => mp.name),
    periodOptions: [
      ...new Set(
        meters.flatMap((mp) => periods(stepOf(mp)).map((p) => p.label))
      ),
    ],
    legend: (["feed", "reported", "gap", "future"] as CellKind[]).map((k) => ({
      fill: CELL[k].fill,
      label: CELL[k].label,
    })),
    monthLegend: [
      { fill: "actualFeed", label: "Actual · every consumption location" },
      { fill: "estimateFuture", label: "Estimated" },
      { fill: "estimateOverdue", label: "Estimated · overdue" },
    ],
  };
};

// ---------------------------------------------------------------------------
// Companies tab (fixture read through the overlay of local edits)
// ---------------------------------------------------------------------------

export const deriveCompanies = (
  domain: DerivedDomain,
  state: DemoStateView
): CompanyView[] => {
  const coEdits = state.companyEdits[domain.key] ?? {};
  return domain.companies.map((x) => {
    const e = coEdits[x.name] ?? {};
    const val = <K extends keyof CompanyInput>(
      field: K,
      fallback: CompanyInput[K]
    ): string =>
      (e as Record<string, string | undefined>)[field] !== undefined
        ? String((e as Record<string, string>)[field])
        : String(fallback);
    const registryCode = val("registryCode", x.registryCode);
    const address = val("address", x.address);
    // domain.meters already carries the Companies-tab overlay (see effectiveMeters), so actual,
    // estimated and total all describe the same edited series.
    const base: MeterRowView[] = domain.meters
      .filter((mp) => mp.entity === x.name)
      .map((mp) => ({
        key: mp.id,
        added: Boolean(mp.added),
        name: mp.name,
        mpid: mp.added ? (mp.mpid ?? "") : (mp.mpid ?? mp.id),
        supplier: mp.supplier,
        sourceLabel: mp.alwaysEstimated ? "Estimated" : mp.source,
        sourceTone: sourceTone(mp),
        actual: mp.actual,
        annual: mp.fy,
        months: [...mp.monthly],
      }));
    const missing: string[] = [];
    if (!registryCode) missing.push("Company registry code missing");
    if (!address) missing.push("Beneficiary address missing");
    if (!base.length) missing.push("No consumption locations");
    const bySupplier = new Map<
      string,
      { supplier: string; ids: string[]; volume: number }
    >();
    for (const r of base) {
      const k = r.supplier || "Not specified";
      const g = bySupplier.get(k) ?? { supplier: k, ids: [], volume: 0 };
      g.ids.push(r.mpid || r.name);
      g.volume += r.annual;
      bySupplier.set(k, g);
    }
    const totActual = sum(base.map((r) => r.actual));
    const totFy = sum(base.map((r) => r.annual));
    return {
      key: x.name,
      name: val("name", x.name),
      registryCode,
      address,
      beneficiaryType: val("beneficiaryType", x.beneficiaryType),
      visibility: val("visibility", x.visibility),
      purpose: val("purpose", x.purpose),
      isReady: missing.length === 0,
      missingLabel: missing.join(" · "),
      meterList: base,
      totals: {
        actual: totActual,
        estimated: Math.max(0, totFy - totActual),
        fy: totFy,
      },
      supplierRoute: domain.supplierRequired,
      // Mirrors the "Establishing cancellation procedures" row of the plan: every supplier in a
      // supplier-coordinated domain is at the same first step in this demo.
      supplierGroups: [...bySupplier.values()].map((g) => ({
        supplier: g.supplier,
        meters: g.ids.join(", "),
        volume: g.volume,
        status: "Establishing contact",
        statusTone: "pending" as const,
      })),
    };
  });
};

// ---------------------------------------------------------------------------
// Month detail modal
// ---------------------------------------------------------------------------

export const deriveMonthDetail = (
  domain: DerivedDomain,
  monthIndex: number
): MonthDetail => ({
  period: `${MONTHS[monthIndex]} 2026`,
  scope: domain.isMonthly ? "monthly portfolio" : "consumption month",
  meters: domain.meters.map((mp) => {
    const estimated = !isActual(mp, monthIndex);
    return {
      id: mp.id,
      name: mp.name,
      entity: mp.entity,
      basis: estimated
        ? "Estimated"
        : kindOf(mp, monthIndex) === "reported"
          ? mp.source === "Approved upload"
            ? "Approved upload"
            : "Reported"
          : "Connected feed",
      tone: estimated
        ? "warn"
        : mp.source === "Approved upload"
          ? "forest"
          : "ok",
      volume: mp.monthly[monthIndex],
    };
  }),
  total: sum(domain.meters.map((mp) => mp.monthly[monthIndex])),
  month: domain.months?.[monthIndex] ?? null,
});
